import { useMemo, useState } from 'react'
import type { CTA, RupturaDigitalSection } from '../../types/payload'
import { KpiCard } from '../KpiCard'
import { calculateGlobalRupturePctExcludingDigital, excludeDigitalChannel } from '../../utils/rupturaMetrics'
import { ChartExportButton } from '../charts/ChartExportButton'
import { safeBarWidthPct } from '../../utils/barWidth'
import styles from './Section.module.css'

interface Props {
  section: RupturaDigitalSection
  ctas: CTA[]
  onCtaClick: (cta: CTA) => void
}

const CANAL_LABEL: Record<string, string> = {
  farma: 'Farma / Farma',
  atacados: 'Alimentar / Atacado/Atacarejo',
  varejo: 'Alimentar / Supermercado',
  digital_nativo: 'Digital / Digital Nativo',
}

const PANEL_CONFIG = [
  {
    key: 'geral',
    title: '% GERAL',
    valueLabel: 'Média das medianas gerais',
  },
  {
    key: 'mp',
    title: '% MARCA PRÓPRIA',
    valueLabel: 'Média das medianas de MP',
  },
] as const

function clamp01(value: number) {
  return Math.min(Math.max(value, 0), 1)
}

function pctLabel(value: number) {
  return `${(value * 100).toFixed(0)}%`
}

export function RupturaDigital({ section, ctas, onCtaClick }: Props) {
  const nonDigitalRecords = useMemo(() => excludeDigitalChannel(section.records), [section.records])
  const nonDigitalSummaries = useMemo(
    () => excludeDigitalChannel(section.channel_summaries ?? []),
    [section.channel_summaries],
  )
  const globalRupturePctExcludingDigital = useMemo(
    () => calculateGlobalRupturePctExcludingDigital(section.records),
    [section.records],
  )
  const totalChecksExcludingDigital = useMemo(
    () => nonDigitalRecords
      .filter(record => record.metric_scope === 'geral')
      .reduce((sum, record) => sum + record.checks, 0),
    [nonDigitalRecords],
  )
  const channels = Array.from(new Set(nonDigitalRecords.map(record => record.canal)))
  const [activeChannel, setActiveChannel] = useState(channels[0] ?? 'farma')

  const rows = useMemo(() => {
    const channelRecords = nonDigitalRecords.filter(record => record.canal === activeChannel)
    const byBanner = new Map<
      string,
      {
        geral?: number
        mp?: number
        geralAvailability?: number
        mpAvailability?: number
      }
    >()

    channelRecords.forEach(record => {
      const current = byBanner.get(record.bandeira_nome) ?? {}
      if (record.metric_scope === 'geral') {
        current.geral = record.stockout_pct
        current.geralAvailability = clamp01(record.availability_pct ?? 1 - record.stockout_pct)
      } else {
        current.mp = record.stockout_pct
        current.mpAvailability = clamp01(record.availability_pct ?? 1 - record.stockout_pct)
      }
      byBanner.set(record.bandeira_nome, current)
    })

    return Array.from(byBanner.entries())
      .map(([banner, values]) => ({
        banner,
        geral: clamp01(values.geral ?? 0),
        mp: clamp01(values.mp ?? 0),
        geralAvailability: clamp01(values.geralAvailability ?? 1 - (values.geral ?? 0)),
        mpAvailability: clamp01(values.mpAvailability ?? 1 - (values.mp ?? 0)),
      }))
      .sort((left, right) => right.geral - left.geral)
  }, [activeChannel, nonDigitalRecords])

  const activeSummary = useMemo(
    () => nonDigitalSummaries.find(summary => summary.canal === activeChannel) ?? null,
    [activeChannel, nonDigitalSummaries],
  )

  return (
    <section className={`card ${styles.section}`}>
      <div className={styles.sectionHeader}>
        <div>
          <span className="microlabel">Painel de Ruptura Digital</span>
          <h2 className={styles.sectionTitle}>Medianas de ruptura geral e MP por canal</h2>
        </div>
        <div className={styles.keyFigures}>
          <span>{(globalRupturePctExcludingDigital * 100).toFixed(1)}% média simples</span>
          <span>{totalChecksExcludingDigital.toLocaleString('pt-BR')} checks sintéticos</span>
        </div>
      </div>

      <div className={styles.tabRow} role="tablist" aria-label="Canais de ruptura">
        {channels.map(channel => (
          <button
            key={channel}
            type="button"
            role="tab"
            aria-selected={channel === activeChannel}
            className={`${styles.tabChip} ${channel === activeChannel ? styles.tabChipActive : ''}`}
            onClick={() => setActiveChannel(channel)}
          >
            {CANAL_LABEL[channel] ?? channel}
          </button>
        ))}
      </div>

      <div className={styles.legend}>
        <span><i className={styles.legendAvailability} /> Disponibilidade</span>
        <span><i className={styles.legendStockout} /> Ruptura</span>
        <span className={styles.legendExportSlot}>
          <ChartExportButton
            getContainer={() =>
              document.querySelector<HTMLElement>(`[data-chart-container="ruptura-digital-${activeChannel}"]`)
            }
            filename={`ruptura-digital-${activeChannel}.png`}
            label="Exportar gráfico em PNG"
          />
        </span>
      </div>

      <div className={styles.ruptureCompareGrid} data-chart-container={`ruptura-digital-${activeChannel}`}>
        {PANEL_CONFIG.map(panel => {
          const summaryValue = panel.key === 'geral'
            ? activeSummary?.general_median_rupture_pct_mean
            : activeSummary?.mp_median_rupture_pct_mean

          return (
            <article key={panel.key} className={styles.rupturePanel}>
              <div className={styles.rupturePanelHeader}>
                <h3 className={styles.channelTitle}>{panel.title}</h3>
                {typeof summaryValue === 'number' && (
                  <span className={styles.channelStatPill}>
                    {pctLabel(summaryValue)} {panel.valueLabel}
                  </span>
                )}
              </div>

              <div className={styles.rupturePanelRows}>
                {rows.map(row => {
                  const stockoutPct = panel.key === 'geral' ? row.geral : row.mp
                  const availabilityPct = panel.key === 'geral' ? row.geralAvailability : row.mpAvailability
                  const availabilityValue = availabilityPct * 100
                  const stockoutValue = stockoutPct * 100

                  return (
                    <div key={`${panel.key}-${row.banner}`} className={styles.rupturePanelRow}>
                      <div className={styles.rupturePanelLabel}>
                        <strong>{row.banner}</strong>
                      </div>
                      <div className={styles.ruptureBarSlot}>
                        <div
                          className={styles.ruptureStackBar}
                          title={`${row.banner} - Ruptura: ${pctLabel(stockoutPct)}`}
                        >
                          <div className={styles.ruptureStackBarClip}>
                            <div
                              className={styles.ruptureAvailabilitySegment}
                              style={{ width: `${safeBarWidthPct(availabilityValue)}%` }}
                            >
                              <span className={styles.ruptureSegmentValue}>{pctLabel(availabilityPct)}</span>
                            </div>
                            <div
                              className={styles.ruptureStockoutSegment}
                              style={{ width: `${safeBarWidthPct(stockoutValue)}%` }}
                            >
                              {stockoutValue >= 10 && (
                                <span className={styles.ruptureSegmentValue}>{pctLabel(stockoutPct)}</span>
                              )}
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  )
                })}
              </div>
            </article>
          )
        })}
      </div>

      {nonDigitalSummaries.length > 0 && (
        <div className={styles.metricGrid}>
          {nonDigitalSummaries.map(summary => (
            <KpiCard
              key={summary.canal}
              label={CANAL_LABEL[summary.canal] ?? summary.canal}
              value={`${(summary.general_median_rupture_pct_mean * 100).toFixed(1)}%`}
              meta="média das medianas gerais"
              indicator="default"
            />
          ))}
        </div>
      )}

      {section.notes && (
        <ul className={styles.notesList}>
          {section.notes.map(note => <li key={note}>{note}</li>)}
        </ul>
      )}

      {ctas.length > 0 && (
        <div className={styles.ctaRow}>
          {ctas.map(cta => (
            <button key={cta.id} type="button" className="btn-cta" onClick={() => onCtaClick(cta)}>
              {cta.label}
            </button>
          ))}
        </div>
      )}
    </section>
  )
}
